import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Clock, Sparkles } from "lucide-react";

/**
 * Small pill shown in the sidebar footer. Communicates how many days are
 * left in the 30-day free trial (or "Trial ended" once expired). Lifetime-
 * free and paid users see nothing — the pill is deliberately quiet.
 *
 * Colour ramp:
 *   > 7 days   → neutral stone
 *   1-7 days   → warm clay (nudge)
 *   0 days     → red (blocker)
 */
export default function TrialPill() {
  const { billing } = useAuth();
  if (!billing) return null;

  const { status, trial_days_remaining: days } = billing;

  if (status === "lifetime_free" || status === "active") return null;

  const expired = status === "trial_expired";
  const warning = !expired && typeof days === "number" && days <= 7;

  const tone = expired
    ? "bg-red-50 text-red-600 ring-red-200"
    : warning
      ? "bg-hearth-clay/10 text-hearth-clayDark ring-hearth-clay/30"
      : "bg-hearth-stone text-hearth-mute ring-hearth-line";

  const label = expired
    ? "Trial ended"
    : days === 0
      ? "Last day of trial"
      : `${days} day${days === 1 ? "" : "s"} left in trial`;

  return (
    <Link
      to="/upgrade"
      data-testid="trial-pill"
      className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs ring-1 transition hover:brightness-95 ${tone}`}
    >
      <span className="inline-flex items-center gap-1.5 truncate">
        {expired ? <Sparkles size={12} /> : <Clock size={12} />}
        <span className="truncate">{label}</span>
      </span>
      <span className="caption tracking-widest whitespace-nowrap">Upgrade →</span>
    </Link>
  );
}
